// Phase 7 D3 — Track C T-C6 (also consumed by T-C3 Theme).
//
// Single source of truth for `GET /api/users/me/preferences`. Both the
// theme page and the notifications page mount this hook; react-query
// dedupes the request so they share one in-flight call.
//
// On mutation (PATCH) we invalidate the same query key + write the patched
// shape into the cache so the dependent UIs re-render without a second
// round-trip. Mutation errors propagate to the caller — Q-2 pessimistic UI
// means the form keeps the loader on screen until the round-trip resolves.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type {
  UpdatePreferencesBody,
  UserPreferences,
} from '@dailydeck/shared/types';
import { apiClient } from '../app/apiClient.js';

export const PREFERENCES_QUERY_KEY = ['users', 'me', 'preferences'] as const;

export function usePreferences(): UseQueryResult<UserPreferences, Error> {
  return useQuery<UserPreferences, Error>({
    queryKey: PREFERENCES_QUERY_KEY,
    queryFn: () => apiClient.usersMe.getPreferences(),
    staleTime: 30_000,
  });
}

export function useUpdatePreferences(): UseMutationResult<
  UserPreferences,
  Error,
  UpdatePreferencesBody
> {
  const queryClient = useQueryClient();
  return useMutation<UserPreferences, Error, UpdatePreferencesBody>({
    mutationFn: (body) => apiClient.usersMe.updatePreferences(body),
    onSuccess: (next) => {
      queryClient.setQueryData<UserPreferences>(PREFERENCES_QUERY_KEY, next);
    },
  });
}
